/**
 * User schemas for validation
 */

import { z } from 'zod';

/**
 * User status enum
 * @deprecated Use isActive boolean instead (schema updated to match database)
 */
export const userStatusSchema = z.enum([
  'active',
  'inactive',
  'suspended',
  'deleted',
]);
export type UserStatus = z.infer<typeof userStatusSchema>;

// ─── Employment type — ONE vocabulary, declared once ─────────────────────────

/**
 * THE employment-type alphabet: every value `users.employment_type` may hold.
 *
 * Foundations 2 (Users rail), B2c-T2. principles.md #1 ("one store, one
 * resolver, one owner per noun"), #3 ("nothing that describes a noun is
 * hardcoded") and #5 ("reference by key, never by name").
 *
 * ── WHAT WAS WRONG ────────────────────────────────────────────────────────
 *
 * There were TWO vocabularies for this one field and a lossy translator
 * between them. The store (`bA1_users_employment_fields.sql`) held
 * `full_time | part_time | contractor | intern`; the browser
 * (`packages/web/src/lib/mock-users-data.ts`) declared
 * `fte | contractor | vendor | partner`; and two hand-written maps bridged
 * them — `apiToUiEmploymentType` in `use-users.ts` and
 * `employmentTypeMap` / `uiToApiEmploymentType` in the create and edit
 * dialogs. Neither map was injective, so the field did not round-trip:
 *
 *   an admin picked "Vendor"   → the API stored `contractor` → the screen
 *                                redrew it as "Contractor";
 *   an admin picked "Partner"  → the API stored `intern`     → the screen
 *                                redrew it as "Partner" (because `intern`
 *                                mapped back to `partner`), so `intern` as
 *                                an ACTUAL value was unreachable and
 *                                unnameable;
 *   `part_time` stored by any   → the screen redrew it as "FTE".
 *   other path
 *
 * ── THE MODEL NOW ─────────────────────────────────────────────────────────
 *
 * One list. c32 widens the `users_employment_type_check` CHECK to exactly
 * these six values, so the store and this schema say the same thing, and
 * `GET /users/field-options` serves them to the browser — which declares no
 * list of its own and no map. The UI value IS the stored value.
 *
 * `full_time` replaces the browser's `fte`: the store's spelling wins,
 * because the store is the thing two surfaces could disagree about.
 */
export const EMPLOYMENT_TYPES = [
  'full_time',
  'part_time',
  'contractor',
  'intern',
  'vendor',
  'partner',
] as const;

export const EmploymentTypeSchema = z.enum(EMPLOYMENT_TYPES);
export type EmploymentType = (typeof EMPLOYMENT_TYPES)[number];

/**
 * How each employment type reads to a PERSON — one declaration, beside the
 * keys it names, for the same reason `LIFECYCLE_STATE_LABELS` sits beside the
 * lifecycle alphabet: the ids are machine vocabulary (`full_time` has an
 * underscore in it) and nothing a screen shows a person is a key.
 *
 * `GET /users/field-options` serves these, so the dropdown, the badge and the
 * review step all read one answer.
 */
export const EMPLOYMENT_TYPE_LABELS: Readonly<
  Record<EmploymentType, string>
> = Object.freeze({
  full_time: 'Full time',
  part_time: 'Part time',
  contractor: 'Contractor',
  intern: 'Intern',
  vendor: 'Vendor',
  partner: 'Partner',
});

/**
 * WHICH employment types are TIME-BOUNDED — the engagements that have a
 * scheduled end.
 *
 * ONE fact, two uses, declared once:
 *
 *   - the Create and Edit forms REQUIRE an end date for these (it is served
 *     on each option as `requiresEndDate`, so the browser asks rather than
 *     re-derives);
 *   - the roster flags these with an employment badge, and leaves the
 *     permanent staff unlabelled.
 *
 * Both used to be `employmentType !== 'fte'`, written out six times across
 * two dialogs and the roster (the field's `required`, its label suffix, its
 * hint line, the wizard's `canProceed`, the review row and the badge
 * condition) — a property of the employment type, re-derived from its key at
 * every call site.
 *
 * Employees (`full_time`, `part_time`) have no scheduled end. Everyone else
 * is engaged for a term.
 */
export const TIME_BOUNDED_EMPLOYMENT_TYPES: readonly EmploymentType[] =
  Object.freeze(['contractor', 'intern', 'vendor', 'partner']);

/**
 * Is this a time-bounded engagement? `null` (never set) is not — an absent
 * value is not a claim that the engagement ends.
 */
export function isTimeBoundedEmployment(
  type: EmploymentType | null | undefined
): boolean {
  if (!type) return false;
  return TIME_BOUNDED_EMPLOYMENT_TYPES.includes(type);
}

/** One option as `GET /users/field-options` serves it. */
export const EmploymentTypeOptionSchema = z.object({
  /** The stored value. The control's value IS this — there is no translation. */
  value: EmploymentTypeSchema,
  /** What the control shows a person. */
  label: z.string(),
  /** Must the employment form carry an end date for this option? */
  requiresEndDate: z.boolean(),
});
export type EmploymentTypeOption = z.infer<typeof EmploymentTypeOptionSchema>;

/**
 * THE option list the API serves and the browser renders.
 *
 * Derived from the alphabet above rather than written out again, so a seventh
 * employment type is one edit in one place.
 */
export const EMPLOYMENT_TYPE_OPTIONS: readonly EmploymentTypeOption[] =
  Object.freeze(
    EMPLOYMENT_TYPES.map((value) =>
      Object.freeze({
        value,
        label: EMPLOYMENT_TYPE_LABELS[value],
        requiresEndDate: isTimeBoundedEmployment(value),
      })
    )
  );

/** The `GET /users/field-options` response. */
export const UserFieldOptionsResponseSchema = z.object({
  employmentTypes: z.array(EmploymentTypeOptionSchema),
});
export type UserFieldOptionsResponse = z.infer<
  typeof UserFieldOptionsResponseSchema
>;

/** Email validation with lowercase normalization */
export const emailSchema = z
  .string()
  .email('Invalid email address')
  .max(255)
  .transform((email) => email.toLowerCase().trim());

/** Password requirements: min 8 chars, 1 upper, 1 lower, 1 number */
export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(128, 'Password must be at most 128 characters')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number');

/** Name field validation */
export const nameSchema = z
  .string()
  .min(1, 'Name is required')
  .max(100)
  .trim();

/** Optional name field */
export const optionalNameSchema = z
  .string()
  .max(100)
  .trim()
  .optional()
  .nullable();

/** Create user request */
export const createUserSchema = z.object({
  email: emailSchema,
  password: passwordSchema.optional(), // Optional for invitation-based creation
  firstName: optionalNameSchema,
  lastName: optionalNameSchema,
  sendInvitation: z.boolean().optional(), // If true, send invitation email instead of creating with password
});
export type CreateUserInput = z.infer<typeof createUserSchema>;

/** Update user request */
export const updateUserSchema = z.object({
  firstName: optionalNameSchema,
  lastName: optionalNameSchema,
  isActive: z.boolean().optional(),
});
export type UpdateUserInput = z.infer<typeof updateUserSchema>;

/** User response (excludes password) */
export const userResponseSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  isActive: z.boolean(),
  isEmailVerified: z.boolean(),
  lastLoginAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type UserResponse = z.infer<typeof userResponseSchema>;

/** User list response */
export const userListResponseSchema = z.object({
  data: z.array(userResponseSchema),
  pagination: z.object({
    page: z.number(),
    pageSize: z.number(),
    totalCount: z.number(),
    totalPages: z.number(),
    hasNext: z.boolean(),
    hasPrevious: z.boolean(),
  }),
});
export type UserListResponse = z.infer<typeof userListResponseSchema>;

/** Change password request */
export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required'),
  newPassword: passwordSchema,
});
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

// ─── The forced password change (B2d-T1) ────────────────────────────────────
//
// `users.password_must_change` is raised by the force-password-reset doors and
// by the admin temporary-password reset. While it stands, the account may
// AUTHENTICATE but may not hold a session: `POST /auth/login` answers the
// refusal below — no access token, no refresh token, no context-selection
// token — and the only call that response enables is the exchange.
//
// Both shapes live here, next to `changePasswordSchema`, because the API and
// the login page must not describe the same exchange two ways.

/**
 * What a flagged account receives instead of a session. The ABSENCE of a token
 * is the contract: a response that carried one plus a "you should change your
 * password" hint would leave every non-browser client holding the session the
 * administrator just revoked.
 */
export const passwordChangeRequiredSchema = z.object({
  mustChangePassword: z.literal(true),
  changePasswordToken: z.string(),
});
export type PasswordChangeRequired = z.infer<typeof passwordChangeRequiredSchema>;

/**
 * What the caller posts to `POST /auth/change-password` to spend it. The
 * current password is presented alongside the token: the token proves this is
 * the session that just authenticated, and the password proves who is at the
 * keyboard.
 */
export const forcedPasswordChangeSchema = z.object({
  changePasswordToken: z.string().min(1),
  // CAPPED AT THE SAME 128 `passwordSchema` caps a password at (review
  // B2dT1-BUG-08). `POST /auth/change-password` is on the PUBLIC allowlist —
  // no bearer token — and the handler feeds this value straight into
  // `argon2.verify`. Uncapped, one request could hand argon2id a megabyte
  // (Fastify's default body limit) of work, repeated for the whole shared
  // 20/min IP budget. No credential this platform can mint is longer than 128
  // characters, so nothing a real caller sends is refused by the cap.
  currentPassword: z
    .string()
    .min(1, 'Current password is required')
    .max(128, 'Current password must be at most 128 characters'),
  newPassword: passwordSchema,
});
export type ForcedPasswordChangeInput = z.infer<typeof forcedPasswordChangeSchema>;
